import moment from 'moment'
import Cookies from 'universal-cookie'

import { mixpanelIdentifyUser, mixpanelSwitchToLiveEnvironment } from './mixpanelHelper'
import { SUBSCRIPTION_STATUS } from '../constants/tenantConstants'
import { analyticsUserType } from '../constants/userConstants'

const cookies = new Cookies()

const INTERCOM_APP_ID = 'dfpxiwhb'

/**
 * Identify the signed-in admin to Intercom, Mixpanel and Appcues.
 *
 * Shared by BOTH sign-in paths — `checkWebUser` (workforce) and `checkExternalWebUser`
 * (Entra External ID). It used to be inlined in the workforce path only, so an external
 * admin was invisible to every analytics surface and to support chat.
 *
 * Moved out of `App.js#checkWebUser` with one behaviour change, forced by the external path
 * now reaching this code: `businessPhones?.[0]`. That field has no schema default and external
 * provisioning never sets it, so the old unguarded index threw for an external user with no
 * `mobilePhone`. The workforce path is otherwise identical to before.
 *
 * @param {Object} userData - The user envelope returned by either session bootstrap route.
 */
const bootUserAnalytics = userData => {
  const region = cookies.get('x-region-admin')

  mixpanelIdentifyUser(userData, false)

  if (window.Intercom) {
    let subscriptionStatus = 'free'
    if (userData.tenant?.trialSubscription?.isExpired === false) {
      subscriptionStatus = 'trial'
    } else if (userData.tenant.subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE) {
      subscriptionStatus = 'paid'
    }

    window.Intercom('boot', {
      app_id: INTERCOM_APP_ID,
      name: userData.givenName ? userData.givenName : '',
      user_id: userData._id,
      email: userData.mail,
      'Job Title': userData.jobTitle,
      'Subscription Status': subscriptionStatus,
      'Admin Role': userData.isGlobalAdmin ? 'admin' : 'user',
      created_at: moment(userData.createdAt).unix(),
      'CSM Responsible': userData.tenant ? userData.tenant.csmDetails?.responsibleCSM : '',
      'Sales Responsible': userData.tenant ? userData.tenant.subscriptionDetails?.responsibleSalesPerson : '',
      phone: userData.mobilePhone ? userData.mobilePhone : userData.businessPhones?.[0],
      'Graph User Count': userData.tenant ? userData.tenant.graphTotalUserCount : '',
      'Licensed User Count': userData.tenant ? userData.tenant.licensedUserCount : '',
      'Birthday Recognition Active': userData?.tenant?.settings?.enableBirthdayRecognition ?? false,
      company: {
        company_id: userData.tenant._id,
        name: userData.tenant.displayName
      },
      region: region,
      user_type: analyticsUserType(userData.userType)
    })
  }

  mixpanelSwitchToLiveEnvironment()

  if (window.Appcues) {
    window.Appcues.identify(
      userData._id, // unique, required
      {
        createdAt: parseInt((new Date(userData.createdAt).getTime() / 1000).toFixed(0)), // Unix timestamp of user signup date
        planTier: userData.tenant.subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE ? 'paid' : 'free', // Current user’s plan tier
        role: userData.role > 1 ? 'admin' : 'user', // Current user’s role or permissions
        firstName: userData.displayName, // current user's first name
        companyName: userData.tenant.displayName // Current user’s company name
      }
    )
  }
}

export { bootUserAnalytics }
